// AutomatedHiringPipelineScreen.js
// Full-featured AI Hiring Pipeline — live API, 4 view modes, 12 modals
// JobEng · business/recruitment/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  RefreshControl, ActivityIndicator, Modal, FlatList,
  Dimensions, Alert, TextInput, Switch, Platform,
  KeyboardAvoidingView, StatusBar,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const CACHE_KEY = 'ahp_cache_v1';
const SYNC_INTERVAL = 5 * 60 * 1000; // 5 min

// ─── Stages ─────────────────────────────────────────────────────────────────
const STAGES = [
  { id: 'job-posting',        title: 'Job Postings',     icon: 'briefcase-outline',   color: '#4A90E2', short: 'Posted'    },
  { id: 'applicants',         title: 'Applicants',        icon: 'account-multiple',    color: '#50C878', short: 'Applied'   },
  { id: 'applicant-selection',title: 'AI Shortlisted',   icon: 'account-check',       color: '#FFB84D', short: 'Short.'    },
  { id: 'test-candidates',    title: 'Testing',           icon: 'clipboard-check',     color: '#9B59B6', short: 'Testing'   },
  { id: 'video-interview',    title: 'Video Interview',   icon: 'video',               color: '#E74C3C', short: 'Video'     },
  { id: 'qualified-candidates','title':'AI Qualified',   icon: 'account-star',        color: '#3498DB', short: 'Qualif.'   },
  { id: 'final-interview',    title: 'Final Interview',   icon: 'account-tie',         color: '#1ABC9C', short: 'Final'     },
  { id: 'job-offers',         title: 'Job Offers',        icon: 'email-arrow-right',   color: '#F39C12', short: 'Offered'   },
  { id: 'accepted-offers',    title: 'Accepted Offers',   icon: 'check-circle',        color: '#27AE60', short: 'Accepted'  },
  { id: 'hired',              title: 'Hired',             icon: 'trophy',              color: '#8E44AD', short: 'Hired'     },
];

const INTERVIEW_TYPES = [
  { id: 'video',       label: 'Video Call',       icon: 'video'            },
  { id: 'phone',       label: 'Phone Call',        icon: 'phone'            },
  { id: 'in-person',   label: 'In Person',         icon: 'office-building'  },
  { id: 'technical',   label: 'Technical Round',   icon: 'code-braces'      },
  { id: 'panel',       label: 'Panel Interview',   icon: 'account-group'    },
  { id: 'assessment',  label: 'Assessment Centre', icon: 'clipboard-list'   },
];

const REJECT_REASONS = [
  'Skills not matching', 'Insufficient experience', 'Overqualified',
  'Failed technical assessment', 'Poor cultural fit', 'Salary expectations too high',
  'Not available for start date', 'Communication concerns', 'Better candidates available',
];

const NOTE_TYPES = [
  { id: 'general',    label: 'General',     icon: 'note-text',       color: '#6B7280' },
  { id: 'interview',  label: 'Interview',   icon: 'microphone',      color: '#3B82F6' },
  { id: 'concern',    label: 'Concern',     icon: 'alert-circle',    color: '#EF4444' },
  { id: 'positive',   label: 'Positive',    icon: 'thumb-up',        color: '#10B981' },
  { id: 'follow-up',  label: 'Follow Up',   icon: 'clock-outline',   color: '#F59E0B' },
  { id: 'reference',  label: 'Reference',   icon: 'account-check',   color: '#8B5CF6' },
];

const PRESET_TAGS = [
  { id: 'top-candidate',   label: '⭐ Top Candidate',   color: '#F59E0B' },
  { id: 'fast-track',      label: '🚀 Fast Track',       color: '#10B981' },
  { id: 'remote-ok',       label: '🌍 Remote OK',        color: '#3B82F6' },
  { id: 'needs-followup',  label: '📋 Needs Follow-up', color: '#EF4444' },
  { id: 'negotiating',     label: '💬 Negotiating',      color: '#8B5CF6' },
  { id: 'on-hold',         label: '⏸ On Hold',           color: '#6B7280' },
  { id: 'referred',        label: '👥 Referred',         color: '#06B6D4' },
  { id: 'urgent',          label: '🔥 Urgent',           color: '#EF4444' },
];

const SORT_OPTIONS = [
  { id: 'score_desc',  label: 'AI Score ↓',     icon: 'sort-descending' },
  { id: 'score_asc',   label: 'AI Score ↑',     icon: 'sort-ascending'  },
  { id: 'name_asc',    label: 'Name A→Z',        icon: 'sort-alphabetical-ascending'  },
  { id: 'name_desc',   label: 'Name Z→A',        icon: 'sort-alphabetical-descending' },
  { id: 'date_desc',   label: 'Newest First',    icon: 'calendar-arrow-down' },
  { id: 'date_asc',    label: 'Oldest First',    icon: 'calendar-arrow-up'   },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const scoreColor = (s) => s >= 80 ? '#10B981' : s >= 65 ? '#F59E0B' : s >= 50 ? '#F97316' : '#EF4444';
const scoreLabel = (s) => s >= 80 ? 'Excellent' : s >= 65 ? 'Good' : s >= 50 ? 'Average' : 'Weak';
const avatarColor = (name) => {
  const colors = ['#4A90E2','#50C878','#FFB84D','#9B59B6','#E74C3C','#1ABC9C','#F39C12','#3498DB'];
  let h = 0; for (let c of (name||'?')) h = c.charCodeAt(0) + h;
  return colors[h % colors.length];
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) : '';
const pct = (a, b) => b ? Math.round((a / b) * 100) : 0;
const daysInStage = (d) => d ? Math.floor((Date.now() - new Date(d)) / 86400000) : 0;
const SLA_DAYS = { 'applicants':3,'applicant-selection':5,'test-candidates':7,'video-interview':5,'qualified-candidates':3,'final-interview':7,'job-offers':5,'accepted-offers':3 };
const isSLABreach = (c) => { const days = SLA_DAYS[c.stage]; return days ? daysInStage(c.updatedAt||c.stage_updated_at||c.appliedAt) > days : false; };
const fmtSalary = (v) => v ? (String(v).startsWith('\u00a3') ? v : `\u00a3${Number(String(v).replace(/[^0-9.]/g,'')).toLocaleString() || v}`) : '';

const normalise = (a) => ({
  id:          String(a.id || a.application_id || a._id || Math.random()),
  name:        a.candidate_name || a.name || a.full_name || 'Unknown Candidate',
  email:       a.candidate_email || a.email || '',
  phone:       a.phone || a.candidate_phone || '',
  jobTitle:    a.job_title || a.position || '',
  jobId:       a.job_id || a.job_posting_id || null,
  stage:       mapStage(a.status || a.stage || a.pipeline_stage || 'applicants'),
  score:       typeof a.ai_score === 'number' ? a.ai_score
             : typeof a.score    === 'number' ? a.score : null,
  breakdown:   a.score_breakdown || a.ai_breakdown || null,
  skills:      a.skills || a.candidate_skills || [],
  experience:  a.years_experience || a.experience_years || 0,
  education:   a.education || a.qualification || '',
  location:    a.location || a.candidate_location || '',
  linkedin:    a.linkedin_url || '',
  cv:          a.cv_url || a.resume_url || '',
  notes:       a.notes || [],
  tags:        a.tags || [],
  interviews:  a.interviews || [],
  rating:      a.rating || 0,
  appliedAt:   a.applied_at || a.created_at || new Date().toISOString(),
  updatedAt:   a.updated_at || a.updatedAt || null,
  rejected:    a.status === 'rejected' || a.rejected === true,
  offerSalary: a.offer_salary || null,
  offerStart:  a.offer_start_date || null,
  source:      a.application_source || a.source || 'Direct',
  updatedAt:   a.stage_updated_at || a.updated_at || a.applied_at || '',
  noticePeriod:a.notice_period || '',
  nationality: a.nationality || '',
  referredBy:  a.referred_by || '',
});

const mapStage = (s) => {
  const m = {
    'new':'applicants','applied':'applicants','pending':'applicants','received':'applicants',
    'shortlisted':'applicant-selection','screened':'applicant-selection','ai-shortlisted':'applicant-selection',
    'testing':'test-candidates','test':'test-candidates','assessment':'test-candidates',
    'video':'video-interview','video-interview':'video-interview',
    'qualified':'qualified-candidates','ai-qualified':'qualified-candidates',
    'interview':'final-interview','final-interview':'final-interview','interviewing':'final-interview',
    'offer':'job-offers','offered':'job-offers','job-offer':'job-offers',
    'accepted':'accepted-offers','offer-accepted':'accepted-offers',
    'hired':'hired','onboarding':'hired',
    'job-posting':'job-posting',
  };
  return m[s?.toLowerCase()] || s || 'applicants';
};

// ─── Main Component ──────────────────────────────────────────────────────────
const AutomatedHiringPipelineScreen = ({ navigation }) => {
  // Auth
  const [companyId, setCompanyId]   = useState(null);
  const [authToken, setAuthToken]   = useState(null);

  // Data
  const [applications, setApplications] = useState([]);
  const [jobs,         setJobs]         = useState([]);
  const [stats,        setStats]        = useState({});
  const [lastSync,     setLastSync]     = useState(null);

  // UI
  const [viewMode,     setViewMode]     = useState('stages'); // stages | list | kanban | analytics
  const [isLoading,    setIsLoading]    = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [sortMode,     setSortMode]     = useState('score_desc');
  const [filterStage,  setFilterStage]  = useState(null);
  const [showRejected, setShowRejected] = useState(false);
  const [bulkMode,     setBulkMode]     = useState(false);
  const [selected,     setSelected]     = useState(new Set());
  const [autoProgress, setAutoProgress] = useState(false);

  // Selected items
  const [selStage,     setSelStage]     = useState(null);
  const [selCandidate, setSelCandidate] = useState(null);
  const [selJob,       setSelJob]       = useState(null);

  // Modals
  const [modal, setModal] = useState({
    stage: false, candidate: false, schedule: false, offer: false,
    reject: false, note: false, tag: false, sort: false,
    bulkMove: false, jobDetail: false, filter: false, aiConfig: false,
    email: false, score: false,
    feedback: false, compare: false, export: false,
  });
  const [compareList, setCompareList]   = useState([]);
  const [compareMode, setCompareMode]   = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ technical:0, communication:0, culture:0, overall:0, recommend:'', notes:'' });
  const [filterSLAOnly, setFilterSLAOnly] = useState(false);

  // Form states
  const [scheduleForm, setScheduleForm] = useState({ type:'video', date:'', time:'', link:'', interviewers:'', notes:'' });
  const [offerForm,    setOfferForm]    = useState({ salary:'', start:'', deadline:'', benefits:'', notes:'' });
  const [rejectForm,   setRejectForm]   = useState({ reason:'', note:'' });
  const [noteForm,     setNoteForm]     = useState({ type:'general', text:'' });
  const [tagDraft,     setTagDraft]     = useState([]);
  const [emailForm,    setEmailForm]    = useState({ subject:'', body:'' });
  const [filterForm,   setFilterForm]   = useState({ minScore:0, maxScore:100, minExp:0, stageIds:[] });
  const [processing,   setProcessing]   = useState('');

  const syncTimerRef = useRef(null);
  const searchRef    = useRef(null);

  // ─── Init ────────────────────────────────────────────────────────────────
  useEffect(() => {
    bootstrap();
    return () => { if (syncTimerRef.current) clearInterval(syncTimerRef.current); };
  }, []);

  const bootstrap = async () => {
    try {
      const [cid, tok] = await Promise.all([
        AsyncStorage.getItem('company_id') || AsyncStorage.getItem('business_id'),
        AsyncStorage.getItem('auth_token') || AsyncStorage.getItem('userToken'),
      ]);
      setCompanyId(cid || '1');
      setAuthToken(tok);
      await loadData(cid || '1', tok);
      syncTimerRef.current = setInterval(() => loadData(cid || '1', tok, true), SYNC_INTERVAL);
    } catch (e) {
      console.error('bootstrap error', e);
      setIsLoading(false);
    }
  };

  // ─── API ─────────────────────────────────────────────────────────────────
  const headers = (tok) => ({
    'X-API-Key': API_KEY,
    'Authorization': `Bearer ${tok}`,
    'Content-Type': 'application/json',
  });

  const loadData = async (cid, tok, silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const [appsRes, jobsRes, statsRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/data/applications?company_id=${cid}&limit=500`, { headers: headers(tok) }),
        axios.get(`${API_BASE}/api/data/jobs?company_id=${cid}&status=active&limit=100`,  { headers: headers(tok) }),
        axios.get(`${API_BASE}/api/recruitment/pipeline/stats?company_id=${cid}`,         { headers: headers(tok) }),
      ]);

      let apps = [], jbs = [], st = {};
      if (appsRes.status  === 'fulfilled') apps = (appsRes.value.data?.data   || appsRes.value.data || []).map(normalise);
      if (jobsRes.status  === 'fulfilled') jbs  =  jobsRes.value.data?.data   || jobsRes.value.data || [];
      if (statsRes.status === 'fulfilled') st   =  statsRes.value.data?.stats || statsRes.value.data || {};

      // Fallback: derive stats from apps if API didn't give them
      if (!st.total) {
        const counts = {};
        STAGES.forEach(s => { counts[s.id] = 0; });
        apps.filter(a => !a.rejected).forEach(a => { if (counts[a.stage] !== undefined) counts[a.stage]++; });
        st = { total: apps.length, stageBreakdown: counts, ...st };
      }

      // Cache
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ apps, jbs, st, ts: Date.now() }));

      setApplications(apps);
      setJobs(jbs);
      setStats(st);
      setLastSync(new Date());
    } catch (e) {
      // Load from cache
      try {
        const cached = JSON.parse(await AsyncStorage.getItem(CACHE_KEY) || '{}');
        if (cached.apps) { setApplications(cached.apps); setJobs(cached.jbs || []); setStats(cached.st || {}); }
      } catch {}
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const apiPut = async (path, body) => {
    const res = await axios.put(`${API_BASE}${path}`, body, { headers: headers(authToken) });
    return res.data;
  };
  const apiPost = async (path, body) => {
    const res = await axios.post(`${API_BASE}${path}`, body, { headers: headers(authToken) });
    return res.data;
  };

  // ─── Derived data ─────────────────────────────────────────────────────────
  const pipelineMap = useCallback(() => {
    const map = {};
    STAGES.forEach(s => { map[s.id] = []; });
    let list = applications.filter(a => showRejected || !a.rejected);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(a =>
        String(a.name ?? '').toLowerCase().includes(q) ||
        String(a.email ?? '').toLowerCase().includes(q) ||
        String(a.jobTitle ?? '').toLowerCase().includes(q) ||
        (a.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    // Apply filter form
    if (filterForm.minScore > 0)  list = list.filter(a => a.score === null || a.score >= filterForm.minScore);
    if (filterForm.maxScore < 100) list = list.filter(a => a.score === null || a.score <= filterForm.maxScore);
    if (filterForm.minExp   > 0)  list = list.filter(a => a.experience >= filterForm.minExp);
    // Sort
    list = [...list].sort((a, b) => {
      switch (sortMode) {
        case 'score_desc': return (b.score||0) - (a.score||0);
        case 'score_asc':  return (a.score||0) - (b.score||0);
        case 'name_asc':   return a.name.localeCompare(b.name);
        case 'name_desc':  return b.name.localeCompare(a.name);
        case 'date_desc':  return new Date(b.appliedAt) - new Date(a.appliedAt);
        case 'date_asc':   return new Date(a.appliedAt) - new Date(b.appliedAt);
        default: return 0;
      }
    });
    list.forEach(a => { if (map[a.stage]) map[a.stage].push(a); });
    return map;
  }, [applications, searchQuery, sortMode, showRejected, filterForm]);

  const pipeline  = pipelineMap();
  const totalApps = applications.filter(a => !a.rejected).length;
  const rejected  = applications.filter(a => a.rejected).length;
  const hired     = (pipeline['hired'] || []).length;
  const offers    = (pipeline['job-offers'] || []).length + (pipeline['accepted-offers'] || []).length;
  const hiringRate = pct(hired, totalApps);

  const filteredList = useCallback(() => {
    const all = [];
    Object.values(pipeline).forEach(arr => arr.forEach(a => all.push(a)));
    if (filterStage) return pipeline[filterStage] || [];
    return all;
  }, [pipeline, filterStage]);

  const avgScore = (() => {
    const scored = applications.filter(a => a.score !== null);
    return scored.length ? Math.round(scored.reduce((s, a) => s + a.score, 0) / scored.length) : null;
  })();

  // ─── Actions ──────────────────────────────────────────────────────────────
  const moveCandidate = async (candidateId, toStage) => {
    setProcessing('moving');
    try {
      await apiPut(`/api/data/applications/${candidateId}`, {
        company_id: parseInt(companyId) || 0,
        status: toStage, pipeline_stage: toStage,
      });
      setApplications(prev => prev.map(a => a.id === candidateId ? { ...a, stage: toStage, rejected: false } : a));
      setModal(m => ({ ...m, candidate: false, stage: false }));
      Alert.alert('✅ Moved', `Candidate moved to ${STAGES.find(s => s.id === toStage)?.title}`);
    } catch (e) {
      Alert.alert('Error', 'Could not move candidate. Please try again.');
    } finally { setProcessing(''); }
  };

  const rejectCandidate = async (candidateId, reason, note) => {
    setProcessing('rejecting');
    try {
      await apiPut(`/api/data/applications/${candidateId}`, {
        company_id: parseInt(companyId) || 0,
        status: 'rejected', rejected: true,
        rejection_reason: reason, rejection_note: note,
      });
      setApplications(prev => prev.map(a => a.id === candidateId ? { ...a, rejected: true } : a));
      setModal(m => ({ ...m, reject: false, candidate: false }));
      Alert.alert('Rejected', 'Candidate has been rejected.');
    } catch (e) {
      Alert.alert('Error', 'Could not reject candidate.');
    } finally { setProcessing(''); }
  };

  const scheduleInterview = async () => {
    if (!selCandidate) return;
    if (!scheduleForm.date || !scheduleForm.time) {
      Alert.alert('Required', 'Please enter date and time.'); return;
    }
    setProcessing('scheduling');
    try {
      await apiPost(`/api/recruitment/applicants/${selCandidate.id}/interviews`, {
        company_id: parseInt(companyId) || 0,
        interview_type: scheduleForm.type,
        interview_date: `${scheduleForm.date}T${scheduleForm.time}:00`,
        interview_link: scheduleForm.link,
        interviewers:   scheduleForm.interviewers,
        notes:          scheduleForm.notes,
      });
      const newInterview = { ...scheduleForm, scheduledAt: new Date().toISOString() };
      setApplications(prev => prev.map(a =>
        a.id === selCandidate.id ? { ...a, interviews: [...(a.interviews||[]), newInterview] } : a
      ));
      setSelCandidate(prev => prev ? { ...prev, interviews: [...(prev.interviews||[]), newInterview] } : prev);
      setModal(m => ({ ...m, schedule: false }));
      setScheduleForm({ type:'video', date:'', time:'', link:'', interviewers:'', notes:'' });
      Alert.alert('✅ Scheduled', 'Interview scheduled successfully.');
    } catch (e) {
      Alert.alert('Error', 'Could not schedule interview.');
    } finally { setProcessing(''); }
  };

  const makeOffer = async () => {
    if (!selCandidate) return;
    if (!offerForm.salary) { Alert.alert('Required', 'Please enter salary.'); return; }
    setProcessing('offering');
    try {
      await apiPost(`/api/recruitment/applicants/${selCandidate.id}/offer`, {
        company_id: parseInt(companyId) || 0,
        offer_salary: offerForm.salary,
        start_date:   offerForm.start,
        deadline:     offerForm.deadline,
        benefits:     offerForm.benefits,
        notes:        offerForm.notes,
      });
      await moveCandidate(selCandidate.id, 'job-offers');
      setModal(m => ({ ...m, offer: false }));
      setOfferForm({ salary:'', start:'', deadline:'', benefits:'', notes:'' });
    } catch (e) {
      Alert.alert('Error', 'Could not send offer.');
    } finally { setProcessing(''); }
  };

  const addNote = async () => {
    if (!selCandidate || !noteForm.text.trim()) return;
    setProcessing('noting');
    try {
      await apiPost(`/api/recruitment/applicants/${selCandidate.id}/notes`, {
        company_id: parseInt(companyId) || 0,
        note_type: noteForm.type, note: noteForm.text,
      });
      const newNote = { ...noteForm, createdAt: new Date().toISOString(), author: 'You' };
      setApplications(prev => prev.map(a =>
        a.id === selCandidate.id ? { ...a, notes: [...(a.notes||[]), newNote] } : a
      ));
      setSelCandidate(prev => prev ? { ...prev, notes: [...(prev.notes||[]), newNote] } : prev);
      setModal(m => ({ ...m, note: false }));
      setNoteForm({ type:'general', text:'' });
    } catch (e) {
      Alert.alert('Error', 'Could not save note.');
    } finally { setProcessing(''); }
  };

  const saveTags = async () => {
    if (!selCandidate) return;
    setProcessing('tagging');
    try {
      await apiPut(`/api/data/applications/${selCandidate.id}`, {
        company_id: parseInt(companyId) || 0, tags: tagDraft,
      });
      setApplications(prev => prev.map(a => a.id === selCandidate.id ? { ...a, tags: tagDraft } : a));
      setSelCandidate(prev => prev ? { ...prev, tags: tagDraft } : prev);
      setModal(m => ({ ...m, tag: false }));
    } catch (e) {
      Alert.alert('Error', 'Could not save tags.');
    } finally { setProcessing(''); }
  };

  const rateCandidate = async (candidateId, rating) => {
    try {
      await apiPut(`/api/data/applications/${candidateId}`, {
        company_id: parseInt(companyId) || 0, rating,
      });
      setApplications(prev => prev.map(a => a.id === candidateId ? { ...a, rating } : a));
      if (selCandidate?.id === candidateId) setSelCandidate(prev => ({ ...prev, rating }));
    } catch {}
  };

  const bulkMove = async (toStage) => {
    if (!selected.size) return;
    setProcessing('bulk-moving');
    try {
      await Promise.all([...selected].map(id =>
        apiPut(`/api/data/applications/${id}`, { company_id: parseInt(companyId)||0, status: toStage, pipeline_stage: toStage })
      ));
      setApplications(prev => prev.map(a =>
        selected.has(a.id) ? { ...a, stage: toStage, rejected: false } : a
      ));
      setSelected(new Set());
      setBulkMode(false);
      setModal(m => ({ ...m, bulkMove: false }));
      Alert.alert('✅ Done', `${selected.size} candidates moved to ${STAGES.find(s=>s.id===toStage)?.title}`);
    } catch { Alert.alert('Error', 'Bulk move failed.'); }
    finally { setProcessing(''); }
  };

  const bulkReject = () => {
    if (!selected.size) return;
    Alert.alert('Confirm Reject', `Reject ${selected.size} candidates?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Reject All', style: 'destructive', onPress: async () => {
        setProcessing('bulk-rejecting');
        try {
          await Promise.all([...selected].map(id =>
            apiPut(`/api/data/applications/${id}`, { company_id: parseInt(companyId)||0, status:'rejected', rejected:true })
          ));
          setApplications(prev => prev.map(a => selected.has(a.id) ? { ...a, rejected:true } : a));
          setSelected(new Set()); setBulkMode(false);
        } catch { Alert.alert('Error','Bulk reject failed.'); }
        finally { setProcessing(''); }
      }},
    ]);
  };

  const runAI = async () => {
    setProcessing('ai');
    try {
      await apiPost(`/api/recruitment/ai-process`, { company_id: parseInt(companyId)||0 });
      await loadData(companyId, authToken, true);
      Alert.alert('🤖 AI Complete', 'AI has scored and sorted all candidates.');
    } catch { Alert.alert('Error', 'AI processing failed.'); }
    finally { setProcessing(''); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const openCandidate = (c) => {
    setSelCandidate(c);
    setTagDraft(c.tags || []);
    setModal(m => ({ ...m, candidate: true }));
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#0e4e96','#1e7be2']} style={s.header}>
      <StatusBar barStyle="light-content" />
      <View style={s.headerRow}>
        <TouchableOpacity style={s.iconBtn} onPress={() => navigation.goBack()}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
        </TouchableOpacity>
        <View style={{ flex:1, marginLeft:10 }}>
          <Text style={s.headerTitle}>AI Hiring Pipeline</Text>
          <Text style={s.headerSub}>
            {lastSync ? `Synced ${fmtTime(lastSync)}` : 'Not synced'}  ·  {totalApps} active
          </Text>
        </View>
        <TouchableOpacity style={s.iconBtn} onPress={() => navigation.navigate('AutomationDashboard')}>
          <MaterialCommunityIcons name="chart-line" size={22} color="#FFF" />
        </TouchableOpacity>
        <TouchableOpacity style={[s.iconBtn,{marginLeft:6}]} onPress={() => setModal(m=>({...m,filter:true}))}>
          <MaterialCommunityIcons name="filter-variant" size={22} color="#FFF" />
        </TouchableOpacity>
      </View>

      {/* Stats pills */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.statsPills}>
        {[
          { label:'Total',    val:totalApps,    color:'rgba(255,255,255,0.2)' },
          { label:'Offered',  val:offers,       color:'rgba(243,156,18,0.4)' },
          { label:'Hired',    val:hired,        color:'rgba(39,174,96,0.4)'  },
          { label:'Rejected', val:rejected,     color:'rgba(231,76,60,0.4)'  },
          { label:'Avg Score',val: avgScore !== null ? `${avgScore}%` : '—', color:'rgba(155,89,182,0.4)' },
          { label:'Rate',     val:`${hiringRate}%`, color:'rgba(52,152,219,0.4)' },
        ].map(p => (
          <View key={p.label} style={[s.pill, { backgroundColor: p.color }]}>
            <Text style={s.pillVal}>{p.val}</Text>
            <Text style={s.pillLabel}>{p.label}</Text>
          </View>
        ))}
      </ScrollView>
    </LinearGradient>
  );

  const renderToolbar = () => (
    <View style={s.toolbar}>
      {/* View mode buttons */}
      {[
        { id:'stages',    icon:'view-list'       },
        { id:'list',      icon:'format-list-bulleted' },
        { id:'kanban',    icon:'view-column'     },
        { id:'analytics', icon:'chart-bar'       },
      ].map(v => (
        <TouchableOpacity
          key={v.id}
          style={[s.toolBtn, viewMode===v.id && s.toolBtnActive]}
          onPress={() => setViewMode(v.id)}
        >
          <MaterialCommunityIcons name={v.icon} size={20} color={viewMode===v.id ? '#0e4e96' : '#999'} />
        </TouchableOpacity>
      ))}

      <View style={s.toolSep} />

      <TouchableOpacity style={s.toolBtn} onPress={() => setModal(m=>({...m,sort:true}))}>
        <MaterialCommunityIcons name="sort" size={20} color="#666" />
      </TouchableOpacity>

      <TouchableOpacity
        style={[s.toolBtn, bulkMode && s.toolBtnActive]}
        onPress={() => { setBulkMode(!bulkMode); setSelected(new Set()); }}
      >
        <MaterialCommunityIcons name="checkbox-multiple-marked-outline" size={20} color={bulkMode?'#0e4e96':'#666'} />
      </TouchableOpacity>

      <TouchableOpacity
        style={[s.toolBtn, showRejected && s.toolBtnActive]}
        onPress={() => setShowRejected(!showRejected)}
      >
        <MaterialCommunityIcons name="account-remove" size={20} color={showRejected?'#EF4444':'#666'} />
      </TouchableOpacity>

      <TouchableOpacity
        style={[s.toolBtn, processing==='ai' && s.toolBtnActive]}
        onPress={runAI}
        disabled={!!processing}
      >
        {processing==='ai'
          ? <ActivityIndicator size={16} color="#0e4e96" />
          : <MaterialCommunityIcons name="robot" size={20} color="#666" />
        }
      </TouchableOpacity>

      <TouchableOpacity
        style={s.toolBtn}
        onPress={() => loadData(companyId, authToken, true)}
        disabled={isRefreshing}
      >
        <MaterialCommunityIcons name="refresh" size={20} color="#666" />
      </TouchableOpacity>
    </View>
  );

  const renderSearch = () => (
    <View style={s.searchRow}>
      <MaterialCommunityIcons name="magnify" size={20} color="#999" style={{ marginLeft:12 }} />
      <TextInput
        ref={searchRef}
        style={s.searchInput}
        placeholder="Search candidates, jobs, tags…"
        placeholderTextColor="#999"
        value={searchQuery}
        onChangeText={setSearchQuery}
      />
      {searchQuery ? (
        <TouchableOpacity onPress={() => setSearchQuery('')} style={{ padding:10 }}>
          <MaterialCommunityIcons name="close" size={18} color="#999" />
        </TouchableOpacity>
      ) : null}
    </View>
  );

  // Bulk action bar
  const renderBulkBar = () => bulkMode && selected.size > 0 && (
    <View style={s.bulkBar}>
      <Text style={s.bulkCount}>{selected.size} selected</Text>
      <TouchableOpacity style={s.bulkBtn} onPress={() => setModal(m=>({...m,bulkMove:true}))}>
        <MaterialCommunityIcons name="transfer" size={16} color="#FFF" />
        <Text style={s.bulkBtnText}>Move</Text>
      </TouchableOpacity>
      <TouchableOpacity style={[s.bulkBtn,{backgroundColor:'#EF4444'}]} onPress={bulkReject}>
        <MaterialCommunityIcons name="account-remove" size={16} color="#FFF" />
        <Text style={s.bulkBtnText}>Reject</Text>
      </TouchableOpacity>
      <TouchableOpacity style={[s.bulkBtn,{backgroundColor:'#6B7280'}]} onPress={() => { setSelected(new Set()); setBulkMode(false); }}>
        <Text style={s.bulkBtnText}>Cancel</Text>
      </TouchableOpacity>
    </View>
  );

  // ─── Stages view ──────────────────────────────────────────────────────────
  const renderStagesView = () => (
    <ScrollView
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={() => { setIsRefreshing(true); loadData(companyId, authToken, true); }} colors={['#0e4e96']} />}
      contentContainerStyle={{ paddingBottom:100 }}
    >
      {STAGES.map((stage, idx) => {
        const items  = pipeline[stage.id] || [];
        const count  = items.length;
        const prevCount = idx > 0 ? (pipeline[STAGES[idx-1].id]||[]).length : totalApps;
        const conv   = prevCount > 0 ? pct(count, prevCount) : 0;
        const top3   = items.slice(0, 3);

        return (
          <TouchableOpacity key={stage.id} style={s.stageCard} onPress={() => { setSelStage(stage); setModal(m=>({...m,stage:true})); }}>
            <View style={[s.stageLeft, { borderLeftColor: stage.color }]}>
              <View style={[s.stageIcon, { backgroundColor: stage.color }]}>
                <MaterialCommunityIcons name={stage.icon} size={22} color="#FFF" />
              </View>
              <View style={{ flex:1, marginLeft:12 }}>
                <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
                  <Text style={s.stageTitle}>{stage.title}</Text>
                  <View style={[s.stageBadge, { backgroundColor: stage.color }]}>
                    <Text style={s.stageBadgeText}>{count}</Text>
                  </View>
                </View>

                {/* Conversion rate bar */}
                <View style={s.convRow}>
                  <View style={s.convBarBg}>
                    <View style={[s.convBarFill, { width:`${Math.min(conv,100)}%`, backgroundColor: stage.color }]} />
                  </View>
                  <Text style={s.convText}>{idx === 0 ? '100%' : `${conv}%`}</Text>
                </View>

                {/* Avatar previews */}
                {top3.length > 0 && (
                  <View style={s.avatarRow}>
                    {top3.map((c, i) => (
                      <View key={c.id} style={[s.miniAvatar, { backgroundColor: avatarColor(c.name), marginLeft: i>0 ? -8 : 0, zIndex: 3-i }]}>
                        <Text style={s.miniAvatarText}>{c.name?.charAt(0)}</Text>
                      </View>
                    ))}
                    {count > 3 && <Text style={s.moreText}>+{count-3}</Text>}
                    {items.some(c => c.score !== null) && (
                      <Text style={[s.stageAvgScore, { color: stage.color }]}>
                        avg {Math.round(items.filter(c=>c.score!==null).reduce((a,c)=>a+c.score,0)/Math.max(items.filter(c=>c.score!==null).length,1))}%
                      </Text>
                    )}
                  </View>
                )}
              </View>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#CCC" style={{ marginLeft:8 }} />
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  // ─── List view ────────────────────────────────────────────────────────────
  const renderListView = () => {
    const list = filteredList();
    return (
      <View style={{ flex:1 }}>
        {/* Stage filter chips */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.chipRow}>
          <TouchableOpacity style={[s.chip, !filterStage && s.chipActive]} onPress={() => setFilterStage(null)}>
            <Text style={[s.chipText, !filterStage && s.chipTextActive]}>All ({totalApps})</Text>
          </TouchableOpacity>
          {STAGES.map(st => {
            const cnt = (pipeline[st.id]||[]).length;
            return (
              <TouchableOpacity key={st.id} style={[s.chip, filterStage===st.id && s.chipActive, { borderColor: st.color }]} onPress={() => setFilterStage(filterStage===st.id ? null : st.id)}>
                <Text style={[s.chipText, filterStage===st.id && s.chipTextActive]}>{st.short} ({cnt})</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        <FlatList
          data={list}
          keyExtractor={item => item.id}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={() => { setIsRefreshing(true); loadData(companyId, authToken, true); }} />}
          renderItem={({ item }) => renderCandidateRow(item)}
          ListEmptyComponent={renderEmpty('No candidates found')}
          contentContainerStyle={{ paddingBottom:100 }}
        />
      </View>
    );
  };

  const renderCandidateRow = (item) => {
    const stageObj  = STAGES.find(st => st.id === item.stage);
    const isSelecting = bulkMode;
    const isChosen  = selected.has(item.id);

    return (
      <TouchableOpacity
        style={[s.candidateRow, item.rejected && s.candidateRowRejected, isChosen && s.candidateRowSelected]}
        onPress={() => isSelecting ? toggleSelect(item.id) : openCandidate(item)}
        onLongPress={() => { setBulkMode(true); toggleSelect(item.id); }}
      >
        {isSelecting && (
          <MaterialCommunityIcons name={isChosen ? 'checkbox-marked' : 'checkbox-blank-outline'} size={22} color="#0e4e96" style={{ marginRight:10 }} />
        )}

        <View style={[s.avatar, { backgroundColor: avatarColor(item.name) }]}>
          <Text style={s.avatarText}>{item.name?.charAt(0)?.toUpperCase()}</Text>
        </View>

        <View style={{ flex:1, marginLeft:12 }}>
          <View style={{ flexDirection:'row', alignItems:'center' }}>
            <Text style={s.candidateName} numberOfLines={1}>{item.name}</Text>
            {item.tags?.length > 0 && (
              <View style={[s.tagDot, { backgroundColor: PRESET_TAGS.find(t=>t.id===item.tags[0])?.color||'#6B7280' }]} />
            )}
          </View>
          <Text style={s.candidateEmail} numberOfLines={1}>{item.jobTitle || item.email}</Text>
          <View style={{ flexDirection:'row', alignItems:'center', marginTop:4 }}>
            {stageObj && (
              <View style={[s.stageChip, { backgroundColor: stageObj.color+'22', borderColor: stageObj.color }]}>
                <Text style={[s.stageChipText, { color: stageObj.color }]}>{stageObj.short}</Text>
              </View>
            )}
            {item.experience > 0 && <Text style={s.meta}>{item.experience}y exp</Text>}
            {item.source && <Text style={s.meta}>· {item.source}</Text>}
          </View>
        </View>

        {/* Score ring */}
        {item.score !== null && item.score !== undefined && (
          <View style={[s.scoreRing, { borderColor: scoreColor(item.score) }]}>
            <Text style={[s.scoreRingText, { color: scoreColor(item.score) }]}>{item.score}</Text>
          </View>
        )}

        {/* Star rating */}
        <View style={s.stars}>
          {[1,2,3,4,5].map(i => (
            <TouchableOpacity key={i} onPress={() => rateCandidate(item.id, i)}>
              <MaterialCommunityIcons name={i <= (item.rating||0) ? 'star' : 'star-outline'} size={14} color="#F59E0B" />
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    );
  };

  // ─── Kanban view ──────────────────────────────────────────────────────────
  const renderKanbanView = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator pagingEnabled style={{ flex:1 }}>
      {STAGES.map(stage => {
        const items = pipeline[stage.id] || [];
        const avg   = items.filter(c=>c.score!==null).length
          ? Math.round(items.filter(c=>c.score!==null).reduce((a,c)=>a+c.score,0)/items.filter(c=>c.score!==null).length)
          : null;
        return (
          <View key={stage.id} style={s.kanbanCol}>
            <LinearGradient colors={[stage.color, stage.color+'CC']} style={s.kanbanHeader}>
              <MaterialCommunityIcons name={stage.icon} size={16} color="#FFF" />
              <Text style={s.kanbanHeaderTitle}>{stage.short}</Text>
              <View style={s.kanbanBadge}><Text style={s.kanbanBadgeText}>{items.length}</Text></View>
            </LinearGradient>
            {avg !== null && (
              <Text style={[s.kanbanAvg, { color: stage.color }]}>avg {avg}%</Text>
            )}
            <ScrollView style={{ flex:1 }} contentContainerStyle={{ padding:8, paddingBottom:80 }}>
              {items.map(item => (
                <TouchableOpacity key={item.id} style={s.kanbanCard} onPress={() => openCandidate(item)}>
                  <View style={{ flexDirection:'row', alignItems:'center', marginBottom:6 }}>
                    <View style={[s.kanbanAvatar, { backgroundColor: avatarColor(item.name) }]}>
                      <Text style={s.kanbanAvatarText}>{item.name?.charAt(0)}</Text>
                    </View>
                    <View style={{ flex:1, marginLeft:8 }}>
                      <Text style={s.kanbanName} numberOfLines={1}>{item.name}</Text>
                      <Text style={s.kanbanJob}  numberOfLines={1}>{item.jobTitle}</Text>
                    </View>
                    {item.score !== null && (
                      <Text style={[s.kanbanScore, { color: scoreColor(item.score) }]}>{item.score}%</Text>
                    )}
                  </View>
                  {item.tags?.length > 0 && (
                    <View style={{ flexDirection:'row', flexWrap:'wrap', gap:4 }}>
                      {item.tags.slice(0,2).map(t => {
                        const pt = PRESET_TAGS.find(p=>p.id===t);
                        return pt ? (
                          <Text key={t} style={[s.kanbanTag, { backgroundColor: pt.color+'22', color: pt.color }]}>{pt.label}</Text>
                        ) : null;
                      })}
                    </View>
                  )}
                </TouchableOpacity>
              ))}
              {items.length === 0 && (
                <View style={s.kanbanEmpty}>
                  <MaterialCommunityIcons name="inbox-outline" size={28} color="#CCC" />
                  <Text style={s.kanbanEmptyText}>Empty</Text>
                </View>
              )}
            </ScrollView>
          </View>
        );
      })}
    </ScrollView>
  );

  // ─── Analytics view ───────────────────────────────────────────────────────
  const renderAnalyticsView = () => {
    const funnel = STAGES.slice(1).map((st, i) => {
      const cur  = (pipeline[st.id]||[]).length;
      const prev = (pipeline[STAGES[i].id]||[]).length || 1;
      return { ...st, count: cur, rate: pct(cur, prev) };
    });

    const scoreDistrib = [
      { label:'90-100', count: applications.filter(a=>a.score>=90).length, color:'#10B981' },
      { label:'75-89',  count: applications.filter(a=>a.score>=75&&a.score<90).length, color:'#34D399' },
      { label:'60-74',  count: applications.filter(a=>a.score>=60&&a.score<75).length, color:'#F59E0B' },
      { label:'45-59',  count: applications.filter(a=>a.score>=45&&a.score<60).length, color:'#F97316' },
      { label:'<45',    count: applications.filter(a=>a.score!==null&&a.score<45).length, color:'#EF4444' },
    ];
    const maxDistrib = Math.max(...scoreDistrib.map(d=>d.count), 1);

    const sourceCounts = {};
    applications.forEach(a => { sourceCounts[a.source||'Direct'] = (sourceCounts[a.source||'Direct']||0)+1; });

    return (
      <ScrollView contentContainerStyle={{ padding:16, paddingBottom:100 }}>
        {/* KPI Cards */}
        <View style={s.kpiGrid}>
          {[
            { label:'Total Applicants', val:totalApps,  icon:'account-multiple', color:'#3B82F6' },
            { label:'Hired',            val:hired,       icon:'trophy',           color:'#10B981' },
            { label:'Offer Rate',       val:`${pct(offers,totalApps)}%`, icon:'email-arrow-right', color:'#F59E0B' },
            { label:'Hire Rate',        val:`${hiringRate}%`, icon:'percent',    color:'#8B5CF6' },
            { label:'Avg AI Score',     val: avgScore !== null ? `${avgScore}%` : '—', icon:'brain', color:'#EC4899' },
            { label:'Active Jobs',      val:jobs.length, icon:'briefcase-outline',color:'#06B6D4' },
          ].map(k => (
            <View key={k.label} style={[s.kpiCard, { borderTopColor: k.color }]}>
              <MaterialCommunityIcons name={k.icon} size={24} color={k.color} />
              <Text style={s.kpiVal}>{k.val}</Text>
              <Text style={s.kpiLabel}>{k.label}</Text>
            </View>
          ))}
        </View>

        {/* Pipeline funnel */}
        <View style={s.analyticsCard}>
          <Text style={s.analyticsTitle}>Pipeline Funnel</Text>
          {STAGES.map(st => {
            const cnt  = (pipeline[st.id]||[]).length;
            const barW = totalApps ? pct(cnt, totalApps) : 0;
            return (
              <View key={st.id} style={s.funnelRow}>
                <Text style={s.funnelLabel} numberOfLines={1}>{st.short}</Text>
                <View style={s.funnelBarBg}>
                  <View style={[s.funnelBarFill, { width:`${barW}%`, backgroundColor: st.color }]} />
                </View>
                <Text style={s.funnelCount}>{cnt}</Text>
              </View>
            );
          })}
        </View>

        {/* Score distribution */}
        <View style={s.analyticsCard}>
          <Text style={s.analyticsTitle}>AI Score Distribution</Text>
          <View style={s.distribRow}>
            {scoreDistrib.map(d => (
              <View key={d.label} style={s.distribCol}>
                <View style={s.distribBarBg}>
                  <View style={[s.distribBarFill, { height:`${pct(d.count,maxDistrib)}%`, backgroundColor: d.color }]} />
                </View>
                <Text style={s.distribCount}>{d.count}</Text>
                <Text style={s.distribLabel}>{d.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Conversion rates */}
        <View style={s.analyticsCard}>
          <Text style={s.analyticsTitle}>Stage Conversion Rates</Text>
          {funnel.map(f => (
            <View key={f.id} style={s.convCardRow}>
              <View style={[s.convCardIcon, { backgroundColor: f.color+'22' }]}>
                <MaterialCommunityIcons name={f.icon} size={16} color={f.color} />
              </View>
              <Text style={s.convCardLabel}>{f.title}</Text>
              <View style={s.convCardBarBg}>
                <View style={[s.convCardBarFill, { width:`${f.rate}%`, backgroundColor: f.color }]} />
              </View>
              <Text style={[s.convCardRate, { color: f.color }]}>{f.rate}%</Text>
            </View>
          ))}
        </View>

        {/* Source breakdown */}
        <View style={s.analyticsCard}>
          <Text style={s.analyticsTitle}>Application Sources</Text>
          {Object.entries(sourceCounts).map(([src, cnt]) => (
            <View key={src} style={s.sourceRow}>
              <Text style={s.sourceLabel}>{src}</Text>
              <View style={s.sourceBarBg}>
                <View style={[s.sourceBarFill, { width:`${pct(cnt,totalApps||1)}%`, backgroundColor:'#0e4e96' }]} />
              </View>
              <Text style={s.sourceCount}>{cnt}</Text>
            </View>
          ))}
        </View>

        {/* Auto-progress toggle */}
        <View style={s.analyticsCard}>
          <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
            <View>
              <Text style={s.analyticsTitle}>Auto-Progression</Text>
              <Text style={s.analyticsSub}>Automatically move top-scoring candidates</Text>
            </View>
            <Switch
              value={autoProgress}
              onValueChange={setAutoProgress}
              trackColor={{ false:'#D1D5DB', true:'#BFDBFE' }}
              thumbColor={autoProgress ? '#0e4e96' : '#9CA3AF'}
            />
          </View>
        </View>

        {/* Active jobs list */}
        <View style={s.analyticsCard}>
          <Text style={s.analyticsTitle}>Active Job Postings ({jobs.length})</Text>
          {jobs.slice(0,5).map(j => (
            <TouchableOpacity key={j.id} style={s.jobRow} onPress={() => { setSelJob(j); setModal(m=>({...m,jobDetail:true})); }}>
              <View style={[s.jobIcon, { backgroundColor:'#0e4e96' }]}>
                <MaterialCommunityIcons name="briefcase" size={16} color="#FFF" />
              </View>
              <View style={{ flex:1, marginLeft:10 }}>
                <Text style={s.jobTitle} numberOfLines={1}>{j.title}</Text>
                <Text style={s.jobMeta}>{j.department} · {j.location}</Text>
              </View>
              <Text style={s.jobApps}>{j.applicants||0} apps</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    );
  };

  // ─── Candidate detail modal ───────────────────────────────────────────────
  const renderCandidateModal = () => {
    const c = selCandidate;
    if (!c) return null;
    const stageObj = STAGES.find(st => st.id === c.stage);
    return (
      <Modal visible={modal.candidate} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,candidate:false}))}>
        <View style={s.modalOverlay}>
          <View style={[s.modalSheet, { maxHeight: height * 0.94 }]}>
            {/* Gradient hero */}
            <LinearGradient colors={[stageObj?.color||'#0e4e96', '#0e4e96']} style={s.candidateHero}>
              <TouchableOpacity style={s.closeAbsolute} onPress={() => setModal(m=>({...m,candidate:false}))}>
                <MaterialCommunityIcons name="close" size={22} color="#FFF" />
              </TouchableOpacity>
              <View style={[s.heroAvatar, { backgroundColor: avatarColor(c.name) }]}>
                <Text style={s.heroAvatarText}>{c.name?.charAt(0)?.toUpperCase()}</Text>
              </View>
              <Text style={s.heroName}>{c.name}</Text>
              <Text style={s.heroEmail}>{c.email}</Text>
              {c.jobTitle ? <Text style={s.heroJob}>{c.jobTitle}</Text> : null}

              {/* Stage pill */}
              {stageObj && (
                <View style={[s.heroPill, { backgroundColor: stageObj.color }]}>
                  <MaterialCommunityIcons name={stageObj.icon} size={12} color="#FFF" />
                  <Text style={s.heroPillText}>{stageObj.title}</Text>
                </View>
              )}

              {/* Star rating */}
              <View style={s.heroStars}>
                {[1,2,3,4,5].map(i => (
                  <TouchableOpacity key={i} onPress={() => rateCandidate(c.id, i)}>
                    <MaterialCommunityIcons name={i<=(c.rating||0)?'star':'star-outline'} size={22} color="#FFD700" />
                  </TouchableOpacity>
                ))}
              </View>
            </LinearGradient>

            <ScrollView contentContainerStyle={{ paddingBottom:120 }}>
              {/* Quick action row */}
              <View style={s.quickActions}>
                {[
                  { label:'Schedule', icon:'calendar-plus', color:'#3B82F6', action: () => { setScheduleForm({type:'video',date:'',time:'',link:'',interviewers:'',notes:''}); setModal(m=>({...m,schedule:true})); } },
                  { label:'Offer',    icon:'email-arrow-right', color:'#10B981', action: () => { setOfferForm({salary:'',start:'',deadline:'',benefits:'',notes:''}); setModal(m=>({...m,offer:true})); } },
                  { label:'Note',     icon:'note-plus', color:'#F59E0B', action: () => { setNoteForm({type:'general',text:''}); setModal(m=>({...m,note:true})); } },
                  { label:'Tags',     icon:'tag-multiple', color:'#8B5CF6', action: () => { setTagDraft(c.tags||[]); setModal(m=>({...m,tag:true})); } },
                  { label:'Reject',   icon:'account-remove', color:'#EF4444', action: () => { setRejectForm({reason:'',note:''}); setModal(m=>({...m,reject:true})); } },
                  { label:'Email',    icon:'email-outline', color:'#06B6D4', action: () => { setEmailForm({subject:`Re: Application`,body:`Hi ${c.name},\n\n`}); setModal(m=>({...m,email:true})); } },
                ].map(qa => (
                  <TouchableOpacity key={qa.label} style={s.quickBtn} onPress={qa.action}>
                    <View style={[s.quickBtnIcon, { backgroundColor: qa.color+'22' }]}>
                      <MaterialCommunityIcons name={qa.icon} size={20} color={qa.color} />
                    </View>
                    <Text style={s.quickBtnLabel}>{qa.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* AI Score section */}
              {c.score !== null && c.score !== undefined && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>AI Assessment</Text>
                  <View style={s.scoreOverall}>
                    <View style={[s.scoreBig, { borderColor: scoreColor(c.score) }]}>
                      <Text style={[s.scoreBigVal, { color: scoreColor(c.score) }]}>{c.score}</Text>
                      <Text style={s.scoreBigPct}>/ 100</Text>
                    </View>
                    <View style={{ flex:1, marginLeft:16 }}>
                      <Text style={[s.scoreLabel, { color: scoreColor(c.score), fontSize:18, fontWeight:'bold' }]}>{scoreLabel(c.score)}</Text>
                      <Text style={s.scoreSub}>Applied {fmtDate(c.appliedAt)}</Text>
                      {c.experience > 0 && <Text style={s.scoreSub}>{c.experience} years experience</Text>}
                      {c.location  && <Text style={s.scoreSub}>📍 {c.location}</Text>}
                    </View>
                  </View>

                  {/* Breakdown bars */}
                  {c.breakdown && (
                    <View style={{ marginTop:16 }}>
                      {Object.entries(c.breakdown).map(([k,v]) => (
                        <View key={k} style={s.breakRow}>
                          <Text style={s.breakLabel}>{k.replace(/([A-Z])/g,' $1').replace(/^./,s=>s.toUpperCase())}</Text>
                          <View style={s.breakBarBg}>
                            <View style={[s.breakBarFill, { width:`${v}%`, backgroundColor: scoreColor(v) }]} />
                          </View>
                          <Text style={[s.breakVal, { color: scoreColor(v) }]}>{v}%</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              )}

              {/* Details grid */}
              <View style={s.section}>
                <Text style={s.sectionTitle}>Details</Text>
                <View style={s.detailGrid}>
                  {[
                    { label:'Phone',     val:c.phone     || '—', icon:'phone'              },
                    { label:'Education', val:c.education || '—', icon:'school'             },
                    { label:'Location',  val:c.location  || '—', icon:'map-marker'         },
                    { label:'Experience',val:c.experience ? `${c.experience} years` : '—', icon:'briefcase' },
                    { label:'Source',    val:c.source    || '—', icon:'source-branch'      },
                    { label:'Applied',   val:fmtDate(c.appliedAt), icon:'calendar'         },
                  ].map(d => (
                    <View key={d.label} style={s.detailItem}>
                      <MaterialCommunityIcons name={d.icon} size={14} color="#0e4e96" />
                      <Text style={s.detailLabel}>{d.label}</Text>
                      <Text style={s.detailVal}>{d.val}</Text>
                    </View>
                  ))}
                </View>
              </View>

              {/* Skills */}
              {c.skills?.length > 0 && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>Skills</Text>
                  <View style={s.skillsWrap}>
                    {c.skills.map((sk, i) => (
                      <View key={i} style={s.skillChip}>
                        <Text style={s.skillChipText}>{sk}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Tags */}
              {c.tags?.length > 0 && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>Tags</Text>
                  <View style={s.skillsWrap}>
                    {c.tags.map(t => {
                      const pt = PRESET_TAGS.find(p=>p.id===t);
                      return pt ? (
                        <View key={t} style={[s.skillChip, { backgroundColor: pt.color+'22', borderColor: pt.color }]}>
                          <Text style={[s.skillChipText, { color: pt.color }]}>{pt.label}</Text>
                        </View>
                      ) : null;
                    })}
                  </View>
                </View>
              )}

              {/* Interviews */}
              {c.interviews?.length > 0 && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>Interviews ({c.interviews.length})</Text>
                  {c.interviews.map((iv, i) => (
                    <View key={i} style={s.interviewRow}>
                      <MaterialCommunityIcons name={INTERVIEW_TYPES.find(t=>t.id===iv.type)?.icon||'calendar'} size={20} color="#0e4e96" />
                      <View style={{ flex:1, marginLeft:10 }}>
                        <Text style={s.interviewType}>{INTERVIEW_TYPES.find(t=>t.id===iv.type)?.label||iv.type}</Text>
                        <Text style={s.interviewMeta}>{fmtDate(iv.date||iv.interview_date)} {fmtTime(iv.date||iv.interview_date)}</Text>
                        {iv.link && <Text style={s.interviewLink}>{iv.link}</Text>}
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {/* Notes */}
              {c.notes?.length > 0 && (
                <View style={s.section}>
                  <Text style={s.sectionTitle}>Notes ({c.notes.length})</Text>
                  {c.notes.map((n, i) => {
                    const nt = NOTE_TYPES.find(t=>t.id===n.type||n.note_type);
                    return (
                      <View key={i} style={[s.noteRow, { borderLeftColor: nt?.color||'#6B7280' }]}>
                        <View style={{ flexDirection:'row', alignItems:'center', marginBottom:4 }}>
                          <MaterialCommunityIcons name={nt?.icon||'note'} size={14} color={nt?.color||'#6B7280'} />
                          <Text style={[s.noteType, { color: nt?.color||'#6B7280' }]}>{nt?.label||n.type}</Text>
                          <Text style={s.noteDate}>{fmtDate(n.createdAt)}</Text>
                        </View>
                        <Text style={s.noteText}>{n.text || n.note}</Text>
                      </View>
                    );
                  })}
                </View>
              )}

              {/* Move to stage */}
              <View style={s.section}>
                <Text style={s.sectionTitle}>Move to Stage</Text>
                <View style={s.stageChips}>
                  {STAGES.map(st => (
                    <TouchableOpacity
                      key={st.id}
                      style={[s.moveChip, c.stage===st.id && { backgroundColor: st.color }, { borderColor: st.color }]}
                      onPress={() => c.stage !== st.id && moveCandidate(c.id, st.id)}
                      disabled={processing==='moving'}
                    >
                      <MaterialCommunityIcons name={st.icon} size={14} color={c.stage===st.id ? '#FFF' : st.color} />
                      <Text style={[s.moveChipText, c.stage===st.id && { color:'#FFF' }]}>{st.short}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── Sub-modals ───────────────────────────────────────────────────────────
  const renderScheduleModal = () => (
    <Modal visible={modal.schedule} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,schedule:false}))}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>📅 Schedule Interview</Text>

          <Text style={s.fieldLabel}>Type</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:14 }}>
            {INTERVIEW_TYPES.map(t => (
              <TouchableOpacity key={t.id} style={[s.typeBtn, scheduleForm.type===t.id && s.typeBtnActive]} onPress={() => setScheduleForm(f=>({...f,type:t.id}))}>
                <MaterialCommunityIcons name={t.icon} size={16} color={scheduleForm.type===t.id?'#FFF':'#0e4e96'} />
                <Text style={[s.typeBtnText, scheduleForm.type===t.id && s.typeBtnTextActive]}>{t.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {[
            { label:'Date (YYYY-MM-DD)', key:'date', placeholder:'2026-04-20' },
            { label:'Time (HH:MM)',      key:'time', placeholder:'10:00'      },
            { label:'Meeting Link',      key:'link', placeholder:'https://meet.google.com/…' },
            { label:'Interviewers',      key:'interviewers', placeholder:'John Smith, Jane Doe' },
            { label:'Notes',             key:'notes', placeholder:'Topics to cover…' },
          ].map(f => (
            <View key={f.key}>
              <Text style={s.fieldLabel}>{f.label}</Text>
              <TextInput style={s.field} placeholder={f.placeholder} placeholderTextColor="#BBB"
                value={scheduleForm[f.key]} onChangeText={v => setScheduleForm(p=>({...p,[f.key]:v}))} />
            </View>
          ))}

          <View style={s.formBtns}>
            <TouchableOpacity style={s.formBtnSecondary} onPress={() => setModal(m=>({...m,schedule:false}))}>
              <Text style={s.formBtnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.formBtnPrimary} onPress={scheduleInterview} disabled={processing==='scheduling'}>
              {processing==='scheduling' ? <ActivityIndicator size={16} color="#FFF" /> : <Text style={s.formBtnPrimaryText}>Schedule</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderOfferModal = () => (
    <Modal visible={modal.offer} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,offer:false}))}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>💼 Make an Offer</Text>
          {[
            { label:'Salary (£)',    key:'salary',   placeholder:'45000' },
            { label:'Start Date',    key:'start',    placeholder:'2026-05-01' },
            { label:'Offer Deadline',key:'deadline', placeholder:'2026-04-20' },
            { label:'Benefits',      key:'benefits', placeholder:'Health, pension, remote…' },
            { label:'Notes',         key:'notes',    placeholder:'Any additional notes…' },
          ].map(f => (
            <View key={f.key}>
              <Text style={s.fieldLabel}>{f.label}</Text>
              <TextInput style={s.field} placeholder={f.placeholder} placeholderTextColor="#BBB"
                value={offerForm[f.key]} onChangeText={v => setOfferForm(p=>({...p,[f.key]:v}))}
                keyboardType={f.key==='salary'?'numeric':'default'} />
            </View>
          ))}
          <View style={s.formBtns}>
            <TouchableOpacity style={s.formBtnSecondary} onPress={() => setModal(m=>({...m,offer:false}))}>
              <Text style={s.formBtnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.formBtnPrimary,{backgroundColor:'#10B981'}]} onPress={makeOffer} disabled={processing==='offering'}>
              {processing==='offering' ? <ActivityIndicator size={16} color="#FFF" /> : <Text style={s.formBtnPrimaryText}>Send Offer</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderRejectModal = () => (
    <Modal visible={modal.reject} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,reject:false}))}>
      <View style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>❌ Reject Candidate</Text>
          <Text style={s.fieldLabel}>Reason</Text>
          {REJECT_REASONS.map(r => (
            <TouchableOpacity key={r} style={[s.reasonBtn, rejectForm.reason===r && s.reasonBtnActive]} onPress={() => setRejectForm(f=>({...f,reason:r}))}>
              <MaterialCommunityIcons name={rejectForm.reason===r?'radiobox-marked':'radiobox-blank'} size={18} color={rejectForm.reason===r?'#EF4444':'#9CA3AF'} />
              <Text style={[s.reasonText, rejectForm.reason===r && { color:'#EF4444' }]}>{r}</Text>
            </TouchableOpacity>
          ))}
          <Text style={s.fieldLabel}>Additional note (optional)</Text>
          <TextInput style={[s.field,{height:70}]} placeholder="Any additional feedback…" placeholderTextColor="#BBB" multiline
            value={rejectForm.note} onChangeText={v => setRejectForm(f=>({...f,note:v}))} />
          <View style={s.formBtns}>
            <TouchableOpacity style={s.formBtnSecondary} onPress={() => setModal(m=>({...m,reject:false}))}>
              <Text style={s.formBtnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.formBtnPrimary,{backgroundColor:'#EF4444'}]}
              onPress={() => selCandidate && rejectCandidate(selCandidate.id, rejectForm.reason, rejectForm.note)}
              disabled={!rejectForm.reason || processing==='rejecting'}
            >
              {processing==='rejecting' ? <ActivityIndicator size={16} color="#FFF" /> : <Text style={s.formBtnPrimaryText}>Reject</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderNoteModal = () => (
    <Modal visible={modal.note} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,note:false}))}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>📝 Add Note</Text>
          <Text style={s.fieldLabel}>Type</Text>
          <View style={s.noteTypeRow}>
            {NOTE_TYPES.map(t => (
              <TouchableOpacity key={t.id} style={[s.noteTypeBtn, noteForm.type===t.id && { backgroundColor: t.color+'22', borderColor: t.color }]} onPress={() => setNoteForm(f=>({...f,type:t.id}))}>
                <MaterialCommunityIcons name={t.icon} size={16} color={noteForm.type===t.id ? t.color : '#9CA3AF'} />
                <Text style={[s.noteTypeBtnText, noteForm.type===t.id && { color:t.color }]}>{t.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={s.fieldLabel}>Note</Text>
          <TextInput style={[s.field,{height:100}]} placeholder="Write your note…" placeholderTextColor="#BBB" multiline
            value={noteForm.text} onChangeText={v => setNoteForm(f=>({...f,text:v}))} />
          <View style={s.formBtns}>
            <TouchableOpacity style={s.formBtnSecondary} onPress={() => setModal(m=>({...m,note:false}))}>
              <Text style={s.formBtnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.formBtnPrimary} onPress={addNote} disabled={!noteForm.text.trim() || processing==='noting'}>
              {processing==='noting' ? <ActivityIndicator size={16} color="#FFF" /> : <Text style={s.formBtnPrimaryText}>Save Note</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderTagModal = () => (
    <Modal visible={modal.tag} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,tag:false}))}>
      <View style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>🏷 Manage Tags</Text>
          <View style={s.tagsGrid}>
            {PRESET_TAGS.map(pt => {
              const active = tagDraft.includes(pt.id);
              return (
                <TouchableOpacity key={pt.id} style={[s.tagChip, active && { backgroundColor: pt.color+'33', borderColor: pt.color }]}
                  onPress={() => setTagDraft(d => active ? d.filter(t=>t!==pt.id) : [...d, pt.id])}>
                  <Text style={[s.tagChipText, active && { color: pt.color }]}>{pt.label}</Text>
                  {active && <MaterialCommunityIcons name="check" size={14} color={pt.color} style={{ marginLeft:4 }} />}
                </TouchableOpacity>
              );
            })}
          </View>
          <View style={s.formBtns}>
            <TouchableOpacity style={s.formBtnSecondary} onPress={() => setModal(m=>({...m,tag:false}))}>
              <Text style={s.formBtnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.formBtnPrimary,{backgroundColor:'#8B5CF6'}]} onPress={saveTags} disabled={processing==='tagging'}>
              {processing==='tagging' ? <ActivityIndicator size={16} color="#FFF" /> : <Text style={s.formBtnPrimaryText}>Save Tags</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderSortModal = () => (
    <Modal visible={modal.sort} animationType="fade" transparent onRequestClose={() => setModal(m=>({...m,sort:false}))}>
      <TouchableOpacity style={s.modalOverlay} onPress={() => setModal(m=>({...m,sort:false}))}>
        <View style={[s.formSheet, { maxHeight:400 }]}>
          <Text style={s.formTitle}>Sort Candidates</Text>
          {SORT_OPTIONS.map(o => (
            <TouchableOpacity key={o.id} style={[s.sortRow, sortMode===o.id && s.sortRowActive]} onPress={() => { setSortMode(o.id); setModal(m=>({...m,sort:false})); }}>
              <MaterialCommunityIcons name={o.icon} size={20} color={sortMode===o.id?'#0e4e96':'#6B7280'} />
              <Text style={[s.sortText, sortMode===o.id && s.sortTextActive]}>{o.label}</Text>
              {sortMode===o.id && <MaterialCommunityIcons name="check" size={18} color="#0e4e96" />}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderBulkMoveModal = () => (
    <Modal visible={modal.bulkMove} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,bulkMove:false}))}>
      <View style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>Move {selected.size} Candidates To…</Text>
          <ScrollView style={{ maxHeight:380 }}>
            {STAGES.map(st => (
              <TouchableOpacity key={st.id} style={s.sortRow} onPress={() => bulkMove(st.id)} disabled={processing==='bulk-moving'}>
                <View style={[s.miniStageIcon, { backgroundColor: st.color }]}>
                  <MaterialCommunityIcons name={st.icon} size={14} color="#FFF" />
                </View>
                <Text style={s.sortText}>{st.title}</Text>
                <Text style={s.sortCount}>{(pipeline[st.id]||[]).length}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderStageModal = () => {
    const stage = selStage;
    if (!stage) return null;
    const items = stage.id === 'job-posting' ? jobs : (pipeline[stage.id] || []);
    return (
      <Modal visible={modal.stage} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,stage:false}))}>
        <View style={s.modalOverlay}>
          <View style={[s.modalSheet, { maxHeight: height * 0.85 }]}>
            <LinearGradient colors={[stage.color, stage.color+'BB']} style={s.stageModalHdr}>
              <TouchableOpacity style={s.closeAbsolute} onPress={() => setModal(m=>({...m,stage:false}))}>
                <MaterialCommunityIcons name="close" size={22} color="#FFF" />
              </TouchableOpacity>
              <MaterialCommunityIcons name={stage.icon} size={32} color="#FFF" />
              <Text style={s.stageModalTitle}>{stage.title}</Text>
              <Text style={s.stageModalCount}>{items.length} {stage.id==='job-posting'?'jobs':'candidates'}</Text>
            </LinearGradient>

            <FlatList
              data={items}
              keyExtractor={item => String(item.id)}
              contentContainerStyle={{ paddingBottom:40 }}
              renderItem={({ item }) =>
                stage.id === 'job-posting' ? (
                  <TouchableOpacity style={s.stageListItem} onPress={() => { setSelJob(item); setModal(m=>({...m,stage:false,jobDetail:true})); }}>
                    <View style={[s.avatar, { backgroundColor:'#0e4e96' }]}>
                      <MaterialCommunityIcons name="briefcase" size={18} color="#FFF" />
                    </View>
                    <View style={{ flex:1, marginLeft:12 }}>
                      <Text style={s.candidateName}>{item.title}</Text>
                      <Text style={s.candidateEmail}>{item.department} · {item.location}</Text>
                    </View>
                    <View style={[s.stageChip, { backgroundColor:'#10B98122', borderColor:'#10B981' }]}>
                      <Text style={[s.stageChipText, { color:'#10B981' }]}>{item.status||'active'}</Text>
                    </View>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity style={s.stageListItem} onPress={() => { setModal(m=>({...m,stage:false})); setTimeout(()=>openCandidate(item),200); }}>
                    <View style={[s.avatar, { backgroundColor: avatarColor(item.name) }]}>
                      <Text style={s.avatarText}>{item.name?.charAt(0)}</Text>
                    </View>
                    <View style={{ flex:1, marginLeft:12 }}>
                      <Text style={s.candidateName}>{item.name}</Text>
                      <Text style={s.candidateEmail}>{item.email}</Text>
                    </View>
                    {item.score !== null && item.score !== undefined && (
                      <View style={[s.scoreRing, { borderColor: scoreColor(item.score) }]}>
                        <Text style={[s.scoreRingText, { color: scoreColor(item.score) }]}>{item.score}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                )
              }
              ListEmptyComponent={renderEmpty(stage.id==='job-posting'?'No active jobs':'No candidates in this stage')}
            />
          </View>
        </View>
      </Modal>
    );
  };

  const renderJobDetailModal = () => {
    const j = selJob;
    if (!j) return null;
    return (
      <Modal visible={modal.jobDetail} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,jobDetail:false}))}>
        <View style={s.modalOverlay}>
          <View style={[s.modalSheet, { maxHeight: height * 0.85 }]}>
            <LinearGradient colors={['#0e4e96','#1e7be2']} style={s.stageModalHdr}>
              <TouchableOpacity style={s.closeAbsolute} onPress={() => setModal(m=>({...m,jobDetail:false}))}>
                <MaterialCommunityIcons name="close" size={22} color="#FFF" />
              </TouchableOpacity>
              <MaterialCommunityIcons name="briefcase" size={32} color="#FFF" />
              <Text style={s.stageModalTitle}>{j.title}</Text>
              <Text style={s.stageModalCount}>{j.department} · {j.location}</Text>
            </LinearGradient>
            <ScrollView contentContainerStyle={{ padding:20, paddingBottom:60 }}>
              {[
                { label:'Salary',      val:j.salary_range||j.salary||'—' },
                { label:'Type',        val:j.employment_type||j.job_type||'—' },
                { label:'Experience',  val:j.experience_required||'—'   },
                { label:'Posted',      val:fmtDate(j.created_at)         },
                { label:'Deadline',    val:fmtDate(j.application_deadline)||'—' },
                { label:'Applicants',  val:String(j.applicants||0)        },
              ].map(d => (
                <View key={d.label} style={s.detailItem}>
                  <Text style={s.detailLabel}>{d.label}</Text>
                  <Text style={s.detailVal}>{d.val}</Text>
                </View>
              ))}
              {j.description && (
                <>
                  <Text style={[s.sectionTitle, { marginTop:16 }]}>Description</Text>
                  <Text style={s.descText}>{j.description}</Text>
                </>
              )}
              <TouchableOpacity style={[s.formBtnPrimary,{marginTop:20}]} onPress={() => { setModal(m=>({...m,jobDetail:false})); navigation.navigate('PostJobDetailsScreen', { job:j }); }}>
                <Text style={s.formBtnPrimaryText}>View Full Job Posting</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  const renderEmailModal = () => (
    <Modal visible={modal.email} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,email:false}))}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>✉️ Email Candidate</Text>
          <Text style={s.fieldLabel}>Subject</Text>
          <TextInput style={s.field} value={emailForm.subject} onChangeText={v=>setEmailForm(f=>({...f,subject:v}))} placeholder="Subject" placeholderTextColor="#BBB" />
          <Text style={s.fieldLabel}>Message</Text>
          <TextInput style={[s.field,{height:140}]} value={emailForm.body} onChangeText={v=>setEmailForm(f=>({...f,body:v}))} placeholder="Write your message…" placeholderTextColor="#BBB" multiline />
          <View style={s.formBtns}>
            <TouchableOpacity style={s.formBtnSecondary} onPress={() => setModal(m=>({...m,email:false}))}>
              <Text style={s.formBtnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.formBtnPrimary,{backgroundColor:'#06B6D4'}]} onPress={() => {
              Alert.alert('Email Sent', `Message sent to ${selCandidate?.email}`);
              setModal(m=>({...m,email:false}));
            }}>
              <Text style={s.formBtnPrimaryText}>Send</Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderFilterModal = () => (
    <Modal visible={modal.filter} animationType="slide" transparent onRequestClose={() => setModal(m=>({...m,filter:false}))}>
      <View style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>🔍 Filters</Text>
          <Text style={s.fieldLabel}>Min AI Score: {filterForm.minScore}%</Text>
          <View style={s.sliderRow}>
            {[0,25,50,60,70,80,90].map(v => (
              <TouchableOpacity key={v} style={[s.sliderBtn, filterForm.minScore===v && s.sliderBtnActive]} onPress={() => setFilterForm(f=>({...f,minScore:v}))}>
                <Text style={[s.sliderBtnText, filterForm.minScore===v && s.sliderBtnTextActive]}>{v}%</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={s.fieldLabel}>Min Experience: {filterForm.minExp} years</Text>
          <View style={s.sliderRow}>
            {[0,1,2,3,5,7,10].map(v => (
              <TouchableOpacity key={v} style={[s.sliderBtn, filterForm.minExp===v && s.sliderBtnActive]} onPress={() => setFilterForm(f=>({...f,minExp:v}))}>
                <Text style={[s.sliderBtnText, filterForm.minExp===v && s.sliderBtnTextActive]}>{v}y</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={s.formBtns}>
            <TouchableOpacity style={s.formBtnSecondary} onPress={() => { setFilterForm({minScore:0,maxScore:100,minExp:0,stageIds:[]}); setModal(m=>({...m,filter:false})); }}>
              <Text style={s.formBtnSecondaryText}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.formBtnPrimary} onPress={() => setModal(m=>({...m,filter:false}))}>
              <Text style={s.formBtnPrimaryText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderEmpty = (msg) => (
    <View style={s.emptyState}>
      <MaterialCommunityIcons name="inbox-outline" size={52} color="#D1D5DB" />
      <Text style={s.emptyText}>{msg}</Text>
    </View>
  );

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) return (
    <View style={s.loadingBox}>
      <LinearGradient colors={['#0e4e96','#1e7be2']} style={{ padding:32, borderRadius:20, alignItems:'center' }}>
        <MaterialCommunityIcons name="robot" size={48} color="#FFF" />
        <ActivityIndicator size={36} color="#FFF" style={{ marginTop:16 }} />
        <Text style={{ color:'#FFF', fontSize:18, fontWeight:'bold', marginTop:12 }}>AI Hiring Pipeline</Text>
        <Text style={{ color:'rgba(255,255,255,0.7)', fontSize:14, marginTop:6 }}>Loading pipeline data…</Text>
      </LinearGradient>
    </View>
  );

  // ─── Root render ──────────────────────────────────────────────────────────
  return (
    <View style={s.root}>
      {renderHeader()}
      {renderToolbar()}
      {renderSearch()}
      {renderBulkBar()}

      <View style={{ flex:1 }}>
        {viewMode === 'stages'    && renderStagesView()}
        {viewMode === 'list'      && renderListView()}
        {viewMode === 'kanban'    && renderKanbanView()}
        {viewMode === 'analytics' && renderAnalyticsView()}
      </View>

      {/* Modals */}
      {renderCandidateModal()}
      {renderScheduleModal()}
      {renderOfferModal()}
      {renderRejectModal()}
      {renderNoteModal()}
      {renderTagModal()}
      {renderSortModal()}
      {renderBulkMoveModal()}
      {renderStageModal()}
      {renderJobDetailModal()}
      {renderEmailModal()}
      {renderFilterModal()}
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  root:             { flex:1, backgroundColor:'#F3F4F6' },
  loadingBox:       { flex:1, justifyContent:'center', alignItems:'center', backgroundColor:'#F3F4F6' },
  // Header
  header:           { paddingTop: Platform.OS==='ios'?52:32, paddingBottom:12, paddingHorizontal:16 },
  headerRow:        { flexDirection:'row', alignItems:'center' },
  headerTitle:      { fontSize:20, fontWeight:'bold', color:'#FFF' },
  headerSub:        { fontSize:12, color:'rgba(255,255,255,0.7)', marginTop:2 },
  iconBtn:          { width:38, height:38, borderRadius:19, backgroundColor:'rgba(255,255,255,0.18)', justifyContent:'center', alignItems:'center' },
  statsPills:       { marginTop:12 },
  pill:             { borderRadius:20, paddingHorizontal:14, paddingVertical:8, marginRight:8, alignItems:'center', minWidth:64 },
  pillVal:          { color:'#FFF', fontSize:16, fontWeight:'bold' },
  pillLabel:        { color:'rgba(255,255,255,0.8)', fontSize:10, marginTop:1 },
  // Toolbar
  toolbar:          { flexDirection:'row', backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E5E7EB', paddingHorizontal:12, paddingVertical:8, gap:4, alignItems:'center' },
  toolBtn:          { width:36, height:36, borderRadius:8, justifyContent:'center', alignItems:'center', backgroundColor:'#F9FAFB' },
  toolBtnActive:    { backgroundColor:'#EEF2FF', borderWidth:1, borderColor:'#C7D2FE' },
  toolSep:          { width:1, height:24, backgroundColor:'#E5E7EB', marginHorizontal:4 },
  // Search
  searchRow:        { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', marginHorizontal:12, marginVertical:8, borderRadius:10, borderWidth:1, borderColor:'#E5E7EB', height:42 },
  searchInput:      { flex:1, fontSize:14, color:'#111', paddingHorizontal:8 },
  // Bulk bar
  bulkBar:          { flexDirection:'row', alignItems:'center', backgroundColor:'#1e3a5f', paddingHorizontal:12, paddingVertical:8, gap:8 },
  bulkCount:        { color:'#FFF', fontWeight:'600', flex:1, fontSize:14 },
  bulkBtn:          { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', borderRadius:6, paddingHorizontal:10, paddingVertical:6, gap:4 },
  bulkBtnText:      { color:'#FFF', fontSize:12, fontWeight:'600' },
  // Chip row
  chipRow:          { paddingHorizontal:12, paddingVertical:8, flexGrow:0 },
  chip:             { borderRadius:20, borderWidth:1, borderColor:'#D1D5DB', paddingHorizontal:12, paddingVertical:5, marginRight:8, backgroundColor:'#FFF' },
  chipActive:       { backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  chipText:         { fontSize:12, color:'#6B7280', fontWeight:'500' },
  chipTextActive:   { color:'#FFF' },
  // Stage cards
  stageCard:        { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', marginHorizontal:12, marginVertical:5, borderRadius:14, padding:14, elevation:2, shadowColor:'#000', shadowOpacity:0.06, shadowRadius:4, shadowOffset:{width:0,height:2} },
  stageLeft:        { flex:1, flexDirection:'row', alignItems:'flex-start', borderLeftWidth:3, paddingLeft:10 },
  stageIcon:        { width:44, height:44, borderRadius:22, justifyContent:'center', alignItems:'center' },
  stageTitle:       { fontSize:15, fontWeight:'700', color:'#111' },
  stageBadge:       { borderRadius:12, minWidth:28, height:24, justifyContent:'center', alignItems:'center', paddingHorizontal:6 },
  stageBadgeText:   { color:'#FFF', fontSize:13, fontWeight:'bold' },
  convRow:          { flexDirection:'row', alignItems:'center', marginTop:6, gap:8 },
  convBarBg:        { flex:1, height:5, backgroundColor:'#E5E7EB', borderRadius:3, overflow:'hidden' },
  convBarFill:      { height:'100%', borderRadius:3 },
  convText:         { fontSize:11, color:'#6B7280', width:34, textAlign:'right' },
  avatarRow:        { flexDirection:'row', alignItems:'center', marginTop:8 },
  miniAvatar:       { width:24, height:24, borderRadius:12, justifyContent:'center', alignItems:'center', borderWidth:1.5, borderColor:'#FFF' },
  miniAvatarText:   { color:'#FFF', fontSize:10, fontWeight:'bold' },
  moreText:         { fontSize:12, color:'#9CA3AF', marginLeft:8 },
  stageAvgScore:    { fontSize:11, fontWeight:'bold', marginLeft:'auto' },
  // Candidate row
  candidateRow:     { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', marginHorizontal:12, marginVertical:4, borderRadius:12, padding:12, elevation:1, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:3, shadowOffset:{width:0,height:1} },
  candidateRowRejected:{ opacity:0.5 },
  candidateRowSelected:{ borderWidth:1.5, borderColor:'#0e4e96', backgroundColor:'#EEF2FF' },
  avatar:           { width:44, height:44, borderRadius:22, justifyContent:'center', alignItems:'center' },
  avatarText:       { color:'#FFF', fontSize:18, fontWeight:'bold' },
  candidateName:    { fontSize:15, fontWeight:'600', color:'#111' },
  candidateEmail:   { fontSize:13, color:'#6B7280', marginTop:1 },
  tagDot:           { width:8, height:8, borderRadius:4, marginLeft:6 },
  meta:             { fontSize:11, color:'#9CA3AF', marginLeft:4 },
  stageChip:        { borderRadius:10, borderWidth:1, paddingHorizontal:7, paddingVertical:2, marginRight:4 },
  stageChipText:    { fontSize:10, fontWeight:'600' },
  scoreRing:        { width:40, height:40, borderRadius:20, borderWidth:2, justifyContent:'center', alignItems:'center', marginLeft:8 },
  scoreRingText:    { fontSize:12, fontWeight:'bold' },
  stars:            { flexDirection:'row', marginLeft:6 },
  // Kanban
  kanbanCol:        { width: width * 0.72, backgroundColor:'#F9FAFB', marginRight:2, borderRightWidth:1, borderRightColor:'#E5E7EB' },
  kanbanHeader:     { flexDirection:'row', alignItems:'center', paddingHorizontal:12, paddingVertical:10, gap:6 },
  kanbanHeaderTitle:{ color:'#FFF', fontWeight:'bold', fontSize:13, flex:1 },
  kanbanBadge:      { backgroundColor:'rgba(255,255,255,0.3)', borderRadius:10, paddingHorizontal:6, paddingVertical:2 },
  kanbanBadgeText:  { color:'#FFF', fontSize:12, fontWeight:'bold' },
  kanbanAvg:        { textAlign:'center', fontSize:11, fontWeight:'600', paddingVertical:4, backgroundColor:'#FFF' },
  kanbanCard:       { backgroundColor:'#FFF', borderRadius:10, padding:10, marginBottom:8, elevation:1, shadowColor:'#000', shadowOpacity:0.05, shadowRadius:3, shadowOffset:{width:0,height:1} },
  kanbanAvatar:     { width:34, height:34, borderRadius:17, justifyContent:'center', alignItems:'center' },
  kanbanAvatarText: { color:'#FFF', fontSize:14, fontWeight:'bold' },
  kanbanName:       { fontSize:13, fontWeight:'600', color:'#111' },
  kanbanJob:        { fontSize:11, color:'#9CA3AF', marginTop:1 },
  kanbanScore:      { fontSize:13, fontWeight:'bold' },
  kanbanTag:        { fontSize:10, borderRadius:8, paddingHorizontal:6, paddingVertical:2, fontWeight:'500' },
  kanbanEmpty:      { alignItems:'center', paddingVertical:40 },
  kanbanEmptyText:  { color:'#D1D5DB', marginTop:8, fontSize:13 },
  // Analytics
  kpiGrid:          { flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:10 },
  kpiCard:          { backgroundColor:'#FFF', borderRadius:12, padding:14, alignItems:'center', flex:1, minWidth:(width-52)/2, borderTopWidth:3, elevation:1, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:3, shadowOffset:{width:0,height:1} },
  kpiVal:           { fontSize:22, fontWeight:'bold', color:'#111', marginTop:6 },
  kpiLabel:         { fontSize:11, color:'#6B7280', marginTop:2, textAlign:'center' },
  analyticsCard:    { backgroundColor:'#FFF', borderRadius:14, padding:16, marginBottom:10, elevation:1, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:3, shadowOffset:{width:0,height:1} },
  analyticsTitle:   { fontSize:16, fontWeight:'700', color:'#111', marginBottom:12 },
  analyticsSub:     { fontSize:12, color:'#9CA3AF', marginTop:2 },
  funnelRow:        { flexDirection:'row', alignItems:'center', marginBottom:8, gap:8 },
  funnelLabel:      { fontSize:11, color:'#6B7280', width:54 },
  funnelBarBg:      { flex:1, height:14, backgroundColor:'#F3F4F6', borderRadius:7, overflow:'hidden' },
  funnelBarFill:    { height:'100%', borderRadius:7 },
  funnelCount:      { fontSize:12, fontWeight:'700', color:'#374151', width:24, textAlign:'right' },
  distribRow:       { flexDirection:'row', justifyContent:'space-around', alignItems:'flex-end', height:120 },
  distribCol:       { alignItems:'center', flex:1 },
  distribBarBg:     { width:24, height:100, backgroundColor:'#F3F4F6', borderRadius:4, justifyContent:'flex-end', overflow:'hidden' },
  distribBarFill:   { width:'100%', borderRadius:4 },
  distribCount:     { fontSize:12, fontWeight:'700', color:'#374151', marginTop:4 },
  distribLabel:     { fontSize:10, color:'#9CA3AF', marginTop:2 },
  convCardRow:      { flexDirection:'row', alignItems:'center', marginBottom:8, gap:8 },
  convCardIcon:     { width:28, height:28, borderRadius:14, justifyContent:'center', alignItems:'center' },
  convCardLabel:    { fontSize:12, color:'#374151', width:100 },
  convCardBarBg:    { flex:1, height:8, backgroundColor:'#F3F4F6', borderRadius:4, overflow:'hidden' },
  convCardBarFill:  { height:'100%', borderRadius:4 },
  convCardRate:     { fontSize:12, fontWeight:'700', width:34, textAlign:'right' },
  sourceRow:        { flexDirection:'row', alignItems:'center', marginBottom:8, gap:8 },
  sourceLabel:      { fontSize:12, color:'#6B7280', width:80 },
  sourceBarBg:      { flex:1, height:8, backgroundColor:'#F3F4F6', borderRadius:4, overflow:'hidden' },
  sourceBarFill:    { height:'100%', borderRadius:4 },
  sourceCount:      { fontSize:12, fontWeight:'700', color:'#374151', width:24, textAlign:'right' },
  jobRow:           { flexDirection:'row', alignItems:'center', paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#F3F4F6' },
  jobIcon:          { width:32, height:32, borderRadius:16, justifyContent:'center', alignItems:'center' },
  jobTitle:         { fontSize:14, fontWeight:'600', color:'#111' },
  jobMeta:          { fontSize:12, color:'#9CA3AF', marginTop:1 },
  jobApps:          { fontSize:12, fontWeight:'600', color:'#0e4e96' },
  // Modal base
  modalOverlay:     { flex:1, backgroundColor:'rgba(0,0,0,0.55)', justifyContent:'flex-end' },
  modalSheet:       { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, overflow:'hidden' },
  formSheet:        { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, padding:20, paddingBottom: Platform.OS==='ios'?34:20 },
  formTitle:        { fontSize:20, fontWeight:'bold', color:'#111', marginBottom:16 },
  fieldLabel:       { fontSize:13, fontWeight:'600', color:'#374151', marginBottom:6 },
  field:            { backgroundColor:'#F9FAFB', borderWidth:1, borderColor:'#E5E7EB', borderRadius:10, padding:12, fontSize:14, color:'#111', marginBottom:14 },
  formBtns:         { flexDirection:'row', gap:12, marginTop:8 },
  formBtnPrimary:   { flex:1, backgroundColor:'#0e4e96', borderRadius:12, padding:14, alignItems:'center' },
  formBtnPrimaryText:{ color:'#FFF', fontWeight:'bold', fontSize:15 },
  formBtnSecondary: { flex:1, backgroundColor:'#F3F4F6', borderRadius:12, padding:14, alignItems:'center' },
  formBtnSecondaryText:{ color:'#374151', fontWeight:'600', fontSize:15 },
  // Candidate hero modal
  candidateHero:    { padding:24, alignItems:'center', paddingTop:36 },
  closeAbsolute:    { position:'absolute', right:16, top:16, width:32, height:32, borderRadius:16, backgroundColor:'rgba(255,255,255,0.2)', justifyContent:'center', alignItems:'center' },
  heroAvatar:       { width:80, height:80, borderRadius:40, justifyContent:'center', alignItems:'center', borderWidth:3, borderColor:'rgba(255,255,255,0.5)' },
  heroAvatarText:   { color:'#FFF', fontSize:36, fontWeight:'bold' },
  heroName:         { color:'#FFF', fontSize:22, fontWeight:'bold', marginTop:10 },
  heroEmail:        { color:'rgba(255,255,255,0.8)', fontSize:14, marginTop:2 },
  heroJob:          { color:'rgba(255,255,255,0.7)', fontSize:13, marginTop:2 },
  heroPill:         { flexDirection:'row', alignItems:'center', gap:4, borderRadius:20, paddingHorizontal:12, paddingVertical:4, marginTop:10 },
  heroPillText:     { color:'#FFF', fontSize:12, fontWeight:'600' },
  heroStars:        { flexDirection:'row', gap:4, marginTop:12 },
  quickActions:     { flexDirection:'row', flexWrap:'wrap', padding:16, gap:10 },
  quickBtn:         { alignItems:'center', width:(width-72)/3 },
  quickBtnIcon:     { width:44, height:44, borderRadius:22, justifyContent:'center', alignItems:'center', marginBottom:4 },
  quickBtnLabel:    { fontSize:11, color:'#374151', fontWeight:'500' },
  section:          { paddingHorizontal:16, paddingBottom:16 },
  sectionTitle:     { fontSize:16, fontWeight:'700', color:'#111', marginBottom:12 },
  scoreOverall:     { flexDirection:'row', alignItems:'center', backgroundColor:'#F9FAFB', borderRadius:12, padding:14 },
  scoreBig:         { width:80, height:80, borderRadius:40, borderWidth:4, justifyContent:'center', alignItems:'center' },
  scoreBigVal:      { fontSize:28, fontWeight:'bold' },
  scoreBigPct:      { fontSize:11, color:'#9CA3AF', marginTop:-2 },
  scoreLabel:       { fontSize:14, color:'#6B7280' },
  scoreSub:         { fontSize:12, color:'#9CA3AF', marginTop:3 },
  breakRow:         { flexDirection:'row', alignItems:'center', marginBottom:8, gap:10 },
  breakLabel:       { fontSize:12, color:'#6B7280', width:130 },
  breakBarBg:       { flex:1, height:8, backgroundColor:'#E5E7EB', borderRadius:4, overflow:'hidden' },
  breakBarFill:     { height:'100%', borderRadius:4 },
  breakVal:         { fontSize:12, fontWeight:'700', width:36, textAlign:'right' },
  detailGrid:       { gap:10 },
  detailItem:       { flexDirection:'row', alignItems:'center', gap:8, paddingVertical:4, borderBottomWidth:1, borderBottomColor:'#F3F4F6' },
  detailLabel:      { fontSize:13, color:'#9CA3AF', width:80 },
  detailVal:        { fontSize:13, color:'#374151', fontWeight:'500', flex:1 },
  skillsWrap:       { flexDirection:'row', flexWrap:'wrap', gap:8 },
  skillChip:        { backgroundColor:'#EEF2FF', borderRadius:20, paddingHorizontal:12, paddingVertical:5, borderWidth:1, borderColor:'#C7D2FE' },
  skillChipText:    { fontSize:13, color:'#4338CA', fontWeight:'500' },
  interviewRow:     { flexDirection:'row', alignItems:'flex-start', backgroundColor:'#F9FAFB', borderRadius:10, padding:12, marginBottom:8 },
  interviewType:    { fontSize:14, fontWeight:'600', color:'#111' },
  interviewMeta:    { fontSize:12, color:'#6B7280', marginTop:2 },
  interviewLink:    { fontSize:12, color:'#0e4e96', marginTop:2 },
  noteRow:          { backgroundColor:'#F9FAFB', borderLeftWidth:3, borderRadius:8, padding:12, marginBottom:8 },
  noteType:         { fontSize:11, fontWeight:'600', marginLeft:4 },
  noteDate:         { fontSize:11, color:'#9CA3AF', marginLeft:'auto' },
  noteText:         { fontSize:13, color:'#374151', lineHeight:18 },
  stageChips:       { flexDirection:'row', flexWrap:'wrap', gap:8 },
  moveChip:         { flexDirection:'row', alignItems:'center', gap:4, borderWidth:1, borderRadius:20, paddingHorizontal:10, paddingVertical:6 },
  moveChipText:     { fontSize:12, fontWeight:'500', color:'#374151' },
  // Stage modal
  stageModalHdr:    { padding:24, alignItems:'center', paddingTop:36 },
  stageModalTitle:  { color:'#FFF', fontSize:22, fontWeight:'bold', marginTop:8 },
  stageModalCount:  { color:'rgba(255,255,255,0.8)', fontSize:14, marginTop:4 },
  stageListItem:    { flexDirection:'row', alignItems:'center', padding:14, borderBottomWidth:1, borderBottomColor:'#F3F4F6' },
  // Sort modal
  sortRow:          { flexDirection:'row', alignItems:'center', padding:14, borderBottomWidth:1, borderBottomColor:'#F3F4F6', gap:12 },
  sortRowActive:    { backgroundColor:'#EEF2FF' },
  sortText:         { fontSize:15, color:'#374151', flex:1 },
  sortTextActive:   { color:'#0e4e96', fontWeight:'600' },
  sortCount:        { fontSize:13, color:'#9CA3AF' },
  // Type buttons
  typeBtn:          { flexDirection:'row', alignItems:'center', gap:6, borderRadius:20, borderWidth:1, borderColor:'#D1D5DB', paddingHorizontal:12, paddingVertical:8, marginRight:8, backgroundColor:'#FFF' },
  typeBtnActive:    { backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  typeBtnText:      { fontSize:13, color:'#0e4e96', fontWeight:'500' },
  typeBtnTextActive:{ color:'#FFF' },
  // Reject reasons
  reasonBtn:        { flexDirection:'row', alignItems:'center', padding:12, borderRadius:8, borderWidth:1, borderColor:'#E5E7EB', marginBottom:8, gap:10 },
  reasonBtnActive:  { borderColor:'#EF4444', backgroundColor:'#FEF2F2' },
  reasonText:       { fontSize:14, color:'#374151', flex:1 },
  // Note types
  noteTypeRow:      { flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:14 },
  noteTypeBtn:      { flexDirection:'row', alignItems:'center', gap:4, borderRadius:20, borderWidth:1, borderColor:'#D1D5DB', paddingHorizontal:10, paddingVertical:6, backgroundColor:'#F9FAFB' },
  noteTypeBtnText:  { fontSize:12, color:'#6B7280' },
  // Tags grid
  tagsGrid:         { flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:14 },
  tagChip:          { flexDirection:'row', alignItems:'center', borderRadius:20, borderWidth:1, borderColor:'#D1D5DB', paddingHorizontal:12, paddingVertical:8, backgroundColor:'#F9FAFB' },
  tagChipText:      { fontSize:13, color:'#374151' },
  // Bulk mini icon
  miniStageIcon:    { width:28, height:28, borderRadius:14, justifyContent:'center', alignItems:'center' },
  // Slider buttons
  sliderRow:        { flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:14 },
  sliderBtn:        { borderRadius:20, borderWidth:1, borderColor:'#D1D5DB', paddingHorizontal:12, paddingVertical:6, backgroundColor:'#F9FAFB' },
  sliderBtnActive:  { backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  sliderBtnText:    { fontSize:13, color:'#374151' },
  sliderBtnTextActive:{ color:'#FFF' },
  // Misc
  emptyState:       { alignItems:'center', paddingVertical:60 },
  emptyText:        { color:'#D1D5DB', fontSize:15, marginTop:12 },
  descText:         { fontSize:14, color:'#374151', lineHeight:22 },
});

export default AutomatedHiringPipelineScreen;
